import { useState, useEffect } from 'react';
import { configService } from '@/lib/config-service';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';
import type { FeedbackCollectorParams } from '@/types/feedbackCollector';

/**
 * Custom hook to build the feedback collector component parameters.
 *
 * Sources userId from AuthProvider, customerName from CompanyContext,
 * and apiHost / accountToken / product from the feedback collector config.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Homepage", "Projects")
 * @returns The EvoMfeApp componentParams object, or null if config is not loaded yet
 */
export function useFeedbackCollectorParams(feature: string): FeedbackCollectorParams | null {
  const [params, setParams] = useState<FeedbackCollectorParams | null>(null);
  const { authenticatedUser, instanceId } = useAuth();
  const { currentCompany } = useCompany();

  useEffect(() => {
    let isMounted = true;

    async function buildParams() {
      try {
        const config = await configService.getFeedbackCollectorConfig();

        if (!isMounted) return;

        setParams({
          feature,
          accountToken: config.accountToken,
          apiHost: config.apiHost,
          customerName: currentCompany?.name ?? instanceId ?? 'Unknown',
          userId: authenticatedUser?.email ?? 'Unknown',
          product: config.product,
        });
      } catch (error) {
        console.error('Failed to build feedback collector params:', error);
        setParams(null);
      }
    }

    buildParams();

    return () => {
      isMounted = false;
    };
  }, [feature, authenticatedUser, currentCompany, instanceId]);

  return params;
}
